import { format } from 'date-fns';
import { Button } from 'primereact/button';
import { InputTextarea } from 'primereact/inputtextarea';
import { OverlayPanel } from 'primereact/overlaypanel';
import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { BidNote, bidAnalysisServices } from 'services/bidAnalysis/bidAnalysisServices';
import classes from './NotesPanel.module.scss';

interface NotesPanelProps {
  bidId: number;
  sectionName: string;
  notes: BidNote[];
  onNotesChange: () => void;
  currentUser: string;
  readOnly?: boolean;
}

export interface NotesPanelRef {
  show: (event: React.MouseEvent, target: HTMLElement) => void;
  hide: () => void;
}

const NotesPanel = forwardRef<NotesPanelRef, NotesPanelProps>(
  ({ bidId, sectionName, notes, onNotesChange, currentUser, readOnly = false }, ref) => {
    const op = useRef<OverlayPanel>(null);
    const [newNoteContent, setNewNoteContent] = useState('');
    const [editingNoteId, setEditingNoteId] = useState<number | null>(null);
    const [editingContent, setEditingContent] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    // Expose show/hide methods via ref
    useImperativeHandle(ref, () => ({
      show: (event: React.MouseEvent, target: HTMLElement) => {
        if (op.current) {
          op.current.show(event, target);
          // Ensure panel stays below both navbars after showing
          setTimeout(() => {
            const panel = document.querySelector('.p-overlaypanel') as HTMLElement;
            if (panel) {
              // Main navbar: 56px, Top header: ~80px, Total: ~136px
              const minTopPosition = 140; // Slightly more to ensure clearance
              const currentTop = panel.style.top ? parseInt(panel.style.top.replace('px', '')) : 0;
              if (currentTop < minTopPosition) {
                panel.style.top = `${minTopPosition}px`;
              }
              // Ensure z-index is below both navbars
              panel.style.zIndex = '999';
            }
          }, 0);
        }
      },
      hide: () => {
        if (op.current) {
          op.current.hide();
        }
      },
    }));

    // Filter notes for this section
    const sectionNotes = notes.filter(note => note.section_name === sectionName);

    // Sort by created_at (most recent first)
    const sortedNotes = [...sectionNotes].sort((a, b) => {
      const dateA = new Date(a.created_at || '').getTime();
      const dateB = new Date(b.created_at || '').getTime();
      return dateB - dateA;
    });

    const handleSubmit = async () => {
      if (!newNoteContent.trim()) {
        toast.error('Please enter note content', { autoClose: 5000 });
        return;
      }

      setIsSaving(true);
      try {
        await bidAnalysisServices.createNote({
          bid_master_id: bidId,
          section_name: sectionName,
          content: newNoteContent.trim(),
          // created_by should be taken from authenticated user session on backend
        });
        setNewNoteContent('');
        // Refresh notes immediately, then show toast
        await onNotesChange();
        toast.success('Note added successfully', { autoClose: 5000 });
      } catch (error: any) {
        console.error('Error creating note:', error);
        const errorMessage =
          error?.response?.data?.message || error?.message || 'Failed to create note';
        toast.error(`Failed to create note: ${errorMessage}`, { autoClose: 5000 });
      } finally {
        setIsSaving(false);
      }
    };

    const handleEdit = (note: BidNote) => {
      if (readOnly) return; // Prevent editing in read-only mode
      setEditingNoteId(note.id);
      setEditingContent(note.content);
    };

    const handleSaveEdit = async () => {
      if (!editingNoteId || !editingContent.trim()) {
        toast.error('Please enter note content', { autoClose: 5000 });
        return;
      }

      setIsSaving(true);
      try {
        await bidAnalysisServices.updateNote(editingNoteId, {
          content: editingContent.trim(),
          // last_modified_by should be taken from authenticated user session on backend
        });
        setEditingNoteId(null);
        setEditingContent('');
        // Refresh notes immediately, then show toast
        await onNotesChange();
        toast.success('Note updated successfully', { autoClose: 5000 });
      } catch (error: any) {
        console.error('Error updating note:', error);
        const errorMessage =
          error?.response?.data?.message || error?.message || 'Failed to update note';
        toast.error(`Failed to update note: ${errorMessage}`, { autoClose: 5000 });
      } finally {
        setIsSaving(false);
      }
    };

    const handleCancelEdit = () => {
      setEditingNoteId(null);
      setEditingContent('');
    };

    const getInitials = (name: string): string => {
      if (!name) return '?';
      const parts = name.split(' ');
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    };

    const formatDate = (dateString: string | null | undefined): string => {
      if (!dateString) return '';
      try {
        const date = new Date(dateString);
        return format(date, 'MM/dd/yyyy hh:mm a');
      } catch {
        return dateString;
      }
    };

    // Get avatar color class based on initials (matching PPS style)
    const getAvatarColorClass = (initials: string): string => {
      const hash = initials.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
      const colors = ['purple', 'red', 'green'];
      return colors[hash % colors.length];
    };

    return (
      <OverlayPanel
        ref={op}
        className={classes.notesOverlay}
        style={{ width: '400px', zIndex: 999 }}
        showCloseIcon
        appendTo={typeof document !== 'undefined' ? document.body : undefined}
      >
        <div className={classes.notesContainer}>
          {/* Header - Matching PPS style */}
          <div className={classes.notesHeader}>
            <h3>Notes</h3>
          </div>

          {/* Content */}
          <div className={classes.notesContent}>
            {/* Add Note Input - Matching PPS style - Hidden in read-only mode */}
            {!readOnly && (
              <div className={classes.addNoteForm}>
                <InputTextarea
                  value={newNoteContent}
                  onChange={e => setNewNoteContent(e.target.value)}
                  placeholder="Note..."
                  rows={3}
                  className={classes.addNoteInput}
                  disabled={isSaving}
                />
                <Button
                  label="Submit"
                  icon={!isSaving ? 'pi pi-arrow-right' : undefined}
                  iconPos="right"
                  onClick={handleSubmit}
                  loading={isSaving}
                  disabled={!newNoteContent.trim() || isSaving}
                  className={classes.submitButton}
                />
              </div>
            )}

            {/* Notes List - Scrollable - Matching PPS style */}
            <div className={classes.notesList}>
              {sortedNotes.length === 0 ? (
                <div className={classes.emptyNotes}>No notes available for this section</div>
              ) : (
                sortedNotes.map(note => {
                  const initials = getInitials(note.created_by);
                  const avatarColorClass = getAvatarColorClass(initials);
                  const isEditing = editingNoteId === note.id;

                  return (
                    <div key={note.id} className={classes.noteItem}>
                      <div className={classes.noteHeader}>
                        <div className={`${classes.noteAvatar} ${classes[avatarColorClass]}`}>
                          {initials}
                        </div>
                        <div className={classes.noteContent}>
                          {isEditing && !readOnly ? (
                            <>
                              <InputTextarea
                                value={editingContent}
                                onChange={e => setEditingContent(e.target.value)}
                                rows={3}
                                className={classes.editingNote}
                                autoFocus
                              />
                              <div className={classes.editActions}>
                                <Button
                                  icon="pi pi-check"
                                  onClick={handleSaveEdit}
                                  loading={isSaving}
                                  disabled={!editingContent.trim() || isSaving}
                                  className={classes.saveEditButton}
                                  tooltip="Save"
                                  tooltipOptions={{ position: 'top' }}
                                />
                                <Button
                                  icon="pi pi-times"
                                  onClick={handleCancelEdit}
                                  disabled={isSaving}
                                  className={classes.cancelEditButton}
                                  tooltip="Cancel"
                                  tooltipOptions={{ position: 'top' }}
                                />
                              </div>
                            </>
                          ) : (
                            <>
                              <div className={classes.noteText}>{note.content}</div>
                              <div className={classes.noteDate}>
                                {note.is_edited && note.last_modified_by ? (
                                  <>
                                    Edited by {note.last_modified_by}{' '}
                                    {note.last_modified_date
                                      ? formatDate(note.last_modified_date)
                                      : formatDate(note.updated_at)}
                                  </>
                                ) : (
                                  <>
                                    Created by {note.created_by} {formatDate(note.created_at)}
                                  </>
                                )}
                              </div>
                              {/* Only show edit button if current user is the note creator and not read-only */}
                              {!readOnly && note.created_by === currentUser && (
                                <div className={classes.noteActions}>
                                  <Button
                                    icon="pi pi-pencil"
                                    className={classes.editNoteButton}
                                    onClick={() => handleEdit(note)}
                                    tooltip="Edit note"
                                    tooltipOptions={{ position: 'top' }}
                                  />
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </OverlayPanel>
    );
  }
);

NotesPanel.displayName = 'NotesPanel';

export default NotesPanel;
